import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, X } from "lucide-react";
import { useAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_HIDE_KEY = "trademind:quiz-banner-hidden-at";

/**
 * Erinnert Orientierung-Nutzer:innen an das Wissens-Quiz. Zeigt sich, solange
 * mindestens ein Modul (Level 0) offen ist. Dismissible für 24 h.
 */
export default function QuizBanner() {
  const { user } = useAuth();
  const [progress, setProgress] = useState(null); // {done, total}
  const [dismissed, setDismissed] = useState(() => {
    try {
      const t = Number(localStorage.getItem(LS_HIDE_KEY) || 0);
      return t && Date.now() - t < 24 * 60 * 60 * 1000;
    } catch { return false; }
  });

  const stage = user?.onboarding?.stage;
  const shouldFetch = !!user && !dismissed;

  useEffect(() => {
    if (!shouldFetch) return;
    let cancelled = false;
    axios.get(`${API}/quiz/modules`, { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        const mods = r.data?.modules || [];
        const done = mods.filter((m) => (m.level || 0) > 0).length;
        setProgress({ done, total: mods.length });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [shouldFetch]);

  if (!user || dismissed) return null;
  if (!progress || progress.done >= progress.total) return null;

  const hide = () => {
    try { localStorage.setItem(LS_HIDE_KEY, String(Date.now())); } catch {}
    setDismissed(true);
  };

  return (
    <div
      className="mb-6 rounded-xl border border-primary/40 bg-primary/10 px-4 py-3 flex items-start gap-3"
      data-testid="quiz-banner"
    >
      <Sparkles className="h-5 w-5 text-primary shrink-0 mt-0.5" strokeWidth={1.5} />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-foreground">
          <span className="font-medium">Nimm dir 5 Minuten für dein Wissens-Quiz.</span>{" "}
          <span className="text-muted-foreground">
            {progress.done === 0
              ? "Fünf kurze Themen — dein Coach passt sich danach genau deinem Stand an."
              : `${progress.done} von ${progress.total} Modulen erledigt. Mach da weiter, wo du aufgehört hast.`}
          </span>
        </p>
        <div className="mt-2 flex items-center gap-3 flex-wrap">
          <Link
            to="/app/quiz"
            className="text-sm text-foreground underline underline-offset-4 hover:opacity-80 inline-flex items-center gap-1"
            data-testid="quiz-banner-cta"
          >
            {progress.done === 0 ? "Quiz starten" : "Weitermachen"} <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
          </Link>
          <button
            type="button"
            onClick={hide}
            className="text-xs text-muted-foreground hover:text-foreground"
            data-testid="quiz-banner-later"
          >
            Später
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={hide}
        className="text-muted-foreground hover:text-foreground shrink-0"
        aria-label="Banner schließen"
        data-testid="quiz-banner-close"
      >
        <X className="h-4 w-4" strokeWidth={1.5} />
      </button>
    </div>
  );
}
